import { Fade, Box, Typography, FormGroup, FormControlLabel, Checkbox, Grid, Button, useMediaQuery } from "@mui/material";
import { FC, useEffect, useRef } from "react";

interface Step5Props {
    activeStep: number;
    form: {
        firstName: string,
        lastName: string,
        phone: string,
        address: string,
        furnitureName: string,
        furnitureSymbol: string,
        purchaseDate: string,
        purchaseProof: string,
        serialNumber: string,
        defectCircumstances: string,
        defectDescription: string,
        claimType: string,
        consent1: boolean,
    };
    images: string[];
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBack: () => void;
}

const Step5: FC<Step5Props> = ({
    activeStep,
    form,
    images,
    handleChange,
    handleBack
}) => {
    const stepRef = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery("(max-width:550px)");

    useEffect(() => {
        if (activeStep === 4 && stepRef.current && isMobile) {
            const top = stepRef.current.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top, behavior: "smooth" });
        }
    }, [activeStep, isMobile]);
    return (
        <Fade in={activeStep === 4} unmountOnExit>
            <Box sx={{ display: activeStep === 4 ? "flex" : "none", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6" fontWeight={600} color="#bfa14b" mb={1} ref={stepRef}>
                    Zgody i podsumowanie
                </Typography>
                <Box sx={{ bgcolor: "#f9f6ef", borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Imię:</Typography>
                    <Typography fontWeight={500}>{form.firstName}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Nazwisko:</Typography>
                    <Typography fontWeight={500}>{form.lastName}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Nr telefonu:</Typography>
                    <Typography fontWeight={500}>{form.phone}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Adres:</Typography>
                    <Typography fontWeight={500}>{form.address}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Nazwa mebla:</Typography>
                    <Typography fontWeight={500}>{form.furnitureName}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Symbol mebla:</Typography>
                    <Typography fontWeight={500}>{form.furnitureSymbol}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Data zakupu:</Typography>
                    <Typography fontWeight={500}>{form.purchaseDate}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Dowód zakupu:</Typography>
                    <Typography fontWeight={500}>{form.purchaseProof}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Numer seryjny mebla:</Typography>
                    <Typography fontWeight={500}>{form.serialNumber}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Okoliczności stwierdzenia wady:</Typography>
                    <Typography fontWeight={500}>{form.defectCircumstances}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Opis wady:</Typography>
                    <Typography fontWeight={500}>{form.defectDescription}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Kupujący korzysta z:</Typography>
                    <Typography fontWeight={500}>{form.claimType === "rękojmia" ? "Rękojmia" : "Gwarancja producenta"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" mt={1}>Załączone zdjęcia:</Typography>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                        {images.map((img, idx) => (
                            <Grid item xs={4} key={idx}>
                                <Box
                                    sx={{
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        border: "1px solid #e0e0e0",
                                        boxShadow: "0 1px 4px rgba(191,161,75,0.08)"
                                    }}
                                >
                                    <img
                                        src={img}
                                        alt={`Podsumowanie zdjęcie ${idx + 1}`}
                                        style={{ width: "100%", height: 60, objectFit: "cover", display: "block" }}
                                    />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <FormGroup sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.consent1}
                                onChange={handleChange}
                                name="consent1"
                                sx={{ color: "#bfa14b", '&.Mui-checked': { color: "#bfa14b" } }}
                            />
                        }
                        label={
                            <Box
                                sx={{
                                    fontSize: "0.92rem",
                                    lineHeight: 1.6,
                                    maxHeight: 180,
                                    overflowY: "auto",
                                    whiteSpace: "pre-line",
                                    pr: 1,
                                }}
                            >
                                <b>Wyrażam zgodę na przetwarzanie moich danych osobowych</b> przez administratora danych w celu realizacji roszczeń z umowy sprzedaży.
                                {"\n\n"}
                                Mając na uwadze treść art. 4 oraz art. 6 Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchyleniu dyrektywy 95/46/WE, oświadczam, że podaję dane osobowe dobrowolnie wskazując, że są one zgodne z prawdą.
                                {"\n\n"}
                                Mam pełną świadomość co do możliwości poprawiania (weryfikowania) moich danych oraz prawa do żądania ich usunięcia z systemu administratora. Zapoznałem się z treścią klauzuli informacyjnej, w tym z informacją o celu, sposobach i czasookresie przetwarzania danych osobowych.
                                {"\n\n"}
                                Administratorem danych staje się sprzedawca przyjmujący zgłoszenie roszczenie reklamacyjne. Dane powyższe mogą być przekazane przez administratora podmiotom realizującym reklamacje (gwarant, producent, sprzedawca) tylko i wyłącznie w celu realizacji skierowanego roszczenia, na co wyrażam zgodę. Pozyskane dane nie będą wykorzystywane przez administratora do jakichkolwiek działań marketingowych.
                            </Box>
                        }
                    />
                </FormGroup>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button
                        variant="outlined"
                        sx={{
                            borderColor: "#bfa14b",
                            color: "#bfa14b",
                            fontWeight: 600,
                            borderRadius: 2,
                            "&:hover": { borderColor: "#a88d36", color: "#a88d36" }
                        }}
                        onClick={handleBack}
                    >
                        Wstecz
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            bgcolor: "#bfa14b",
                            color: "#fff",
                            fontWeight: 600,
                            px: 4,
                            borderRadius: 2,
                            boxShadow: "0 2px 8px rgba(191,161,75,0.12)",
                            "&:hover": { bgcolor: "#a88d36" }
                        }}
                        disabled={!form.consent1}
                    >
                        Wyślij reklamację
                    </Button>
                </Box>
            </Box>
        </Fade>
    )
}

export default Step5;